import { Loader } from '@googlemaps/js-api-loader'
import { translate } from 'i18n/i18n'
import type { ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { colorLog } from '../../utils/colorLog'

type GoogleMaps = typeof google.maps

// @googlemaps/js-api-loader is a singleton: constructing a second Loader with
// options that differ from the first throws "Loader must not be called again
// with different options". Two GoogleMap components mounting before
// window.google.maps is populated used to construct one each, and
// getGoogleMapLocale() could resolve differently between the two calls.
//
// Exactly one Loader is constructed per page, and it is kept even after a
// failed load. Rebuilding it on retry would re-read getGoogleMapLocale(), so a
// locale that changed in the meantime would produce different options and
// reintroduce the very throw this exists to prevent.
let loader: Loader | null = null

const getLoader = (): Loader => {
  if (!loader) {
    const locale = translate.getGoogleMapLocale()

    loader = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string,
      language: locale.language,
      region: locale.region,
      version: 'quarterly', // https://developers.google.com/maps/documentation/javascript/versions
    })
  }

  return loader
}

// The in-flight load, shared so concurrent mounts await one request. Cleared on
// failure so a later mount can retry — against the same Loader, so the options
// are guaranteed identical.
let googleMapsPromise: Promise<GoogleMaps> | null = null

const loadGoogleMaps = (): Promise<GoogleMaps> => {
  if (window.google?.maps) return Promise.resolve(window.google.maps)

  if (!googleMapsPromise) {
    googleMapsPromise = getLoader()
      .load()
      .then((google) => google.maps)
      .catch((error) => {
        googleMapsPromise = null

        throw error
      })
  }

  return googleMapsPromise
}

export type GoogleMapProps = {
  mapOptions: google.maps.MapOptions
  height: number | string
  width: number | string
  onInit: (map: google.maps.Map, maps: typeof google.maps) => (() => unknown) | void
  onError?: (error: unknown) => void
  defaultErrorMessage?: ReactNode
  style?: React.CSSProperties
}

export const GoogleMap: React.FC<GoogleMapProps> = (props) => {
  const divRef = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [error, setError] = useState<boolean>(false)
  //#region useGoogleMaps
  const [googleMaps, setGoogleMaps] = useState<GoogleMaps | null>(null)

  useEffect(() => {
    let subscribed = true

    loadGoogleMaps()
      .then((maps) => {
        colorLog('white', `google.maps ${maps.version}`)

        subscribed && setGoogleMaps(maps)
      })
      .catch((error) => {
        if (subscribed) {
          setError(true)

          if (props.onError) {
            props.onError(error)
          }
        }
      })

    return () => {
      subscribed = false
    }
  }, [])
  //#endregion

  //#region Map: create
  useEffect(() => {
    if (!map && googleMaps && divRef.current) {
      setMap(new googleMaps.Map(divRef.current, props.mapOptions))
    }
  }, [map, googleMaps])
  //#endregion

  //#region Map: update
  useEffect(() => {
    if (map && googleMaps && divRef.current) {
      map.setOptions(props.mapOptions)
    }
  }, [props.mapOptions])
  //#endregion

  //#region Map: onInit
  useEffect(() => {
    let destructor: (() => unknown) | void

    if (map && googleMaps) {
      destructor = props.onInit(map, googleMaps)
    }

    return () => {
      if (destructor) {
        destructor()
      }
    }
  }, [map])
  //#endregion

  return (
    <div
      css={{
        '.gm-style iframe + div': {
          border: 'none !important',
          // ^ This is a hack to fix Google Maps issue https://issuetracker.google.com/issues/182937497
          // The issue seems to have started occurring again in 3.55. (I tested 3.54, and it didn't happen.)
        },
        ...props.style,
        'height': props.height,
        'width': props.width,
      }}
      ref={divRef}
    >
      {error && (
        <div
          style={{
            ...props.style,
            height: props.height,
            width: props.width,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {props.defaultErrorMessage ? props.defaultErrorMessage : null}
        </div>
      )}
    </div>
  )
}
